import type ExcelJS from 'exceljs';
import type { BomLine, RfiRow, CoRow } from '../api/client';

// Branded Excel exports (§9). Internal BOM (Button 2, full internal detail), RFI
// log and change-order log. Teal/gold, never navy. exceljs is lazy-loaded.
async function xlsx(): Promise<typeof import('exceljs').default> {
  return (await import('exceljs')).default;
}

const TEAL = 'FF0E7C86', TEAL_DARK = 'FF0A5C64', GOLD = 'FFC9A227', WHITE = 'FFFFFFFF', BLACK = 'FF111111', GRAY = 'FF7A8585';
const fill = (argb: string) => ({ type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb } });

async function brandedWorkbook(title: string, project: string, today: string, headers: string[], rows: (string | number)[][], widths: number[]) {
  const ExcelJS = await xlsx();
  const wb = new ExcelJS.Workbook();
  wb.creator = '3D Technology Services, Inc.';
  const ws = wb.addWorksheet(title.slice(0, 28), { views: [{ state: 'frozen', ySplit: 5 }] });
  const last = headers.length;
  ws.columns = widths.map((w) => ({ width: w }));

  ws.mergeCells(1, 1, 1, last);
  const t = ws.getCell(1, 1); t.value = '3D Technology Services, Inc.   ·   3dtsi.com';
  t.font = { bold: true, size: 14, color: { argb: WHITE } }; t.fill = fill(TEAL); t.alignment = { vertical: 'middle', indent: 1 };
  ws.getRow(1).height = 24;
  ws.mergeCells(2, 1, 2, last);
  const s = ws.getCell(2, 1); s.value = title; s.font = { bold: true, size: 12, color: { argb: BLACK } }; s.fill = fill(GOLD); s.alignment = { indent: 1 };
  ws.mergeCells(3, 1, 3, last);
  ws.getCell(3, 1).value = `Project: ${project}    ·    ${today}`; ws.getCell(3, 1).font = { bold: true };

  const hr = ws.getRow(5);
  headers.forEach((h, i) => { const c = hr.getCell(i + 1); c.value = h; c.font = { bold: true, color: { argb: WHITE } }; c.fill = fill(TEAL_DARK); c.alignment = { wrapText: true }; });
  hr.height = 26;
  rows.forEach((r) => { const row = ws.addRow([]); r.forEach((v, i) => { row.getCell(i + 1).value = v; }); });

  const buf = await wb.xlsx.writeBuffer();
  return new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

const money = (n: number | null | undefined) => (n == null ? '' : Math.round(n * 100) / 100);

export function exportInternalBom(project: string, lines: BomLine[], today: string) {
  const headers = ['System', 'Part #', 'Mfr', 'Description', 'Qty', 'Unit', 'Unit material', 'Ext material', 'Labor u/hrs', 'Ext hrs', 'Labor cat', 'Price source', 'Confidence', 'Reference'];
  const rows = lines.map((l) => [
    l.system ?? '', l.part_number ?? '', l.manufacturer ?? '', l.description ?? '', l.qty ?? 0, l.unit ?? '',
    money(l.unit_material_cost), money(l.extended_material_cost), money(l.labor_unit_hours), money(l.extended_labor_hours),
    l.labor_category ?? '', l.price_source ?? '', l.confidence_score != null ? `${Math.round(l.confidence_score * 100)}%` : '', l.source_reference ?? '',
  ]);
  return brandedWorkbook('Internal BOM (confidential)', project, today, headers, rows,
    [22, 14, 12, 34, 8, 7, 12, 12, 10, 9, 14, 13, 11, 30]);
}

export function exportRfiLog(project: string, rfis: RfiRow[], today: string) {
  const headers = ['#', 'Title', 'Question', 'Why it matters', 'Reference', 'Cost/schedule impact', 'Suggested clarification', 'Status'];
  const rows = rfis.map((r) => [r.number ?? '', r.title ?? '', r.question ?? '', r.reason ?? '', r.source_ref ?? '', r.cost_schedule_impact ?? '', r.suggested_clarification ?? '', r.status ?? '']);
  return brandedWorkbook('RFI Log', project, today, headers, rows, [5, 30, 50, 36, 18, 28, 30, 10]);
}

export function exportCoLog(project: string, cos: CoRow[], today: string) {
  const headers = ['Title', 'Description', 'Why it may be a change', 'Reference', 'System', 'Risk', 'Cost impact', 'Schedule', 'Recommendation'];
  const rows = cos.map((c) => [c.title ?? '', c.description ?? '', c.why ?? '', c.source_ref ?? '', c.affected_system ?? '', c.risk_level ?? '', c.est_cost_impact_placeholder ?? '', c.est_schedule_impact_placeholder ?? '', c.recommendation ?? '']);
  return brandedWorkbook('Potential Change Orders', project, today, headers, rows, [26, 44, 40, 18, 16, 8, 22, 12, 34]);
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}
